import React, { useState, useEffect, useRef } from 'react';
import { getElementsLayout, searchByElements, findFileInMcp, getStoredElements, replaceElements, explainElementsDiff, screenElementMatch, explainNoElementMatches } from '../../services/analyses';
import ReferenceHistory from '../common/ReferenceHistory';
import { downloadFile } from '../../services/folder';
import './ElementsSearchPanel.css';

const TYPE_LABELS = {
  main_view: 'Vista principal',
  section_view: 'Corte/Sección',
  detail_view: 'Detalle',
  auxiliary_view: 'Vista auxiliar',
  title_block: 'Cajetín',
  manual: 'Zona manual',
};

const TYPE_COLORS = {
  main_view: '#e6194b',
  section_view: '#3cb44b',
  detail_view: '#4363d8',
  auxiliary_view: '#f58231',
  title_block: '#911eb4',
  manual: '#2c3e50',
};

const MIN_BOX_SIDE = 20;

// Criba con IA: el parecido entre recortes satura en 0,99 y no ordena, así que es la IA quien dice
// cuáles valen. Marca y degrada, nunca borra: también se puede equivocar.
const VERDICT_LABELS = { SI: 'Coincide', DUDA: 'Dudoso', NO: 'Descartado' };
const VERDICT_RANK = { SI: 0, DUDA: 1, NO: 2 };
const ELEMENT_BATCH = 10;  // cuántos candidatos más se miran en cada pasada de profundización

const blobUrlToB64 = async (blobUrl) => {
  const blob = await fetch(blobUrl).then((r) => r.blob());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Tope de seguridad, no un encogido general: una zona que ocupe la página entera de un plano
// grande sale a 3.000 px y eso son megas de subida y un montón de tokens de entrada para nada.
// No se baja más porque el modelo tiene que poder LEER las cotas, que es de lo que vive la criba.
const MAX_CROP_SIDE = 1600;

const cropImageToB64 = (imageUrl, bbox) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const [x1, y1, x2, y2] = bbox;
    const sx = (x1 / 1000) * img.naturalWidth;
    const sy = (y1 / 1000) * img.naturalHeight;
    const sw = ((x2 - x1) / 1000) * img.naturalWidth;
    const sh = ((y2 - y1) / 1000) * img.naturalHeight;
    const escala = Math.min(1, MAX_CROP_SIDE / Math.max(sw, sh));
    const canvas = document.createElement('canvas');
    canvas.width = sw * escala;
    canvas.height = sh * escala;
    canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    resolve(canvas.toDataURL('image/jpeg', 0.9).split(',')[1]);
  };
  img.onerror = reject;
  img.src = imageUrl;
});

// El careo llega como "- Coincide: … / - Difiere: … / - Veredicto: …". Se parte en renglones
// etiquetados; si el modelo contesta de otra forma, se enseña tal cual y no se pierde nada.
const DIFF_FIELDS = ['Coincide', 'Difiere', 'Veredicto'];

const parseDiff = (texto) => {
  const filas = [];
  (texto || '').split('\n').forEach((linea) => {
    // El modelo a veces responde "- **Coincide:** …": fuera viñetas y negritas antes de partir
    const limpia = linea.replace(/\*\*/g, '').replace(/^[-*•#\s]+/, '').trim();
    if (!limpia) return;
    const campo = DIFF_FIELDS.find((f) => limpia.toLowerCase().startsWith(`${f.toLowerCase()}:`));
    if (campo) filas.push({ campo, texto: limpia.slice(campo.length + 1).trim() });
    else if (filas.length) filas[filas.length - 1].texto += ` ${limpia}`;
  });
  return filas;
};

const DiffCard = ({ text }) => {
  const filas = parseDiff(text);
  if (!filas.length) return <div className="elements-zoom-diff-text">{text}</div>;
  return (
    <div className="elements-zoom-diff-text">
      {filas.map((fila) => (
        <div key={fila.campo} className={`elements-diff-row campo-${fila.campo.toLowerCase()}`}>
          <span className="elements-diff-campo">{fila.campo}</span>
          <span className="elements-diff-valor">{fila.texto}</span>
        </div>
      ))}
    </div>
  );
};

const fetchPageImage = async (analysisHash, page) => {
  const url = `${process.env.REACT_APP_API_URL}/v1/analyses/${analysisHash}/image${page ? `?page=${page}` : ''}`;
  const response = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  if (!response.ok) return null;
  return URL.createObjectURL(await response.blob());
};

/** Las páginas de los candidatos se cargan aquí, no en cada tarjeta: la miniatura las necesita para
 *  pintarse y la criba con IA para recortar la zona, y así se descargan una sola vez. */
const useResultImages = (results, busqueda) => {
  // `para` dice de QUÉ búsqueda son estas imágenes: en el primer render con resultados nuevos las
  // imágenes todavía son las de la búsqueda anterior, y si un candidato se repite entre búsquedas
  // se acabaría recortando sobre una URL ya liberada.
  // La lista CRECE cuando hay que mirar más a fondo, así que solo se bajan las que falten.
  // En `images` entra TODO lo intentado: la URL si se pudo bajar y null si no. Así se distingue
  // "aún no le toca" de "no hay imagen", que es lo que decide si un resultado puede cribarse.
  // Con un `done` suelto no bastaba: al crecer la lista seguía valiendo true del lote anterior y
  // los nuevos se daban por perdidos antes de que llegara su página.
  const [estado, setEstado] = useState({ para: null, images: {} });
  const urlsRef = useRef({});
  const intentadosRef = useRef(new Set());
  const busquedaRef = useRef(null);

  useEffect(() => () => { Object.values(urlsRef.current).forEach(URL.revokeObjectURL); }, []);

  useEffect(() => {
    if (busquedaRef.current !== busqueda) {
      Object.values(urlsRef.current).forEach(URL.revokeObjectURL);
      urlsRef.current = {};
      intentadosRef.current = new Set();
      busquedaRef.current = busqueda;
      setEstado({ para: busqueda, images: {} });
    }
    if (!results?.length) return undefined;
    const faltan = results.filter((r) => !intentadosRef.current.has(r.analysis_hash));
    if (!faltan.length) return undefined;
    faltan.forEach((r) => intentadosRef.current.add(r.analysis_hash));

    let vivo = true;
    faltan.forEach(async (r) => {
      const url = await fetchPageImage(r.analysis_hash, r.matched_elements?.[0]?.page_number).catch(() => null);
      if (!vivo || busquedaRef.current !== busqueda) { if (url) URL.revokeObjectURL(url); return; }
      if (url) urlsRef.current[r.analysis_hash] = url;
      setEstado((prev) => ({ ...prev, para: busqueda, images: { ...prev.images, [r.analysis_hash]: url } }));
    });
    return () => { vivo = false; };
  }, [results, busqueda]);

  return estado;
};

const ResultCard = ({ result, selection, onZoom, blobUrl, screening }) => {
  const page = result.matched_elements?.[0]?.page_number;
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [mcpPath, setMcpPath] = useState(() => {
    try {
      const metadata = result.metadata_json ? JSON.parse(result.metadata_json) : {};
      return metadata.mcp_path || null;
    } catch (e) {
      return null;
    }
  });

  const handleDownload = async () => {
    setDownloadError(null);
    setDownloading(true);
    try {
      let blob;
      if (mcpPath) {
        try { blob = await downloadFile(mcpPath); } catch { blob = null; }
      }
      if (!blob) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/v1/analyses/${result.analysis_hash}/original`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (response.ok) blob = await response.blob();
      }
      if (!blob && result.source === 'mcp') {
        const found = await findFileInMcp(result.analysis_hash);
        if (found.found) {
          setMcpPath(found.new_path);
          blob = await downloadFile(found.new_path);
        }
      }
      if (!blob) {
        setDownloadError('No se ha podido encontrar el archivo.');
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError('Error al descargar el archivo.');
    } finally {
      setDownloading(false);
    }
  };

  const handleGoToLocation = () => {
    if (!mcpPath) return;
    const folderPath = mcpPath.substring(0, mcpPath.lastIndexOf('/')) || '/';
    const fileName = mcpPath.substring(mcpPath.lastIndexOf('/') + 1);
    // Pestaña nueva: no desmontar el modal ni el ComparisonView
    window.open(`/folder-browser?path=${encodeURIComponent(folderPath)}&highlight=${encodeURIComponent(fileName)}`, '_blank');
  };

  // Solo se pinta el distintivo si el veredicto es uno de los tres conocidos: si el modelo
  // devolviera cualquier otra cosa, la tarjeta se queda como estaba y no se cae el modal.
  const verdict = VERDICT_LABELS[screening?.verdict] ? screening.verdict : null;
  return (
    <div className={`elements-result-card${verdict ? ` veredicto-${verdict.toLowerCase()}` : ''}`}>
      {verdict && (
        <div className={`elements-verdict veredicto-${verdict.toLowerCase()}`}>
          <span className="elements-verdict-label">{VERDICT_LABELS[verdict]}</span>
          {screening.reason && <span className="elements-verdict-reason">{screening.reason}</span>}
        </div>
      )}
      {blobUrl ? (
        <img
          className="elements-result-thumb"
          src={blobUrl}
          alt={result.filename}
          onClick={() => onZoom({ url: blobUrl, filename: result.filename, matched: result.matched_elements, page })}
          title="Ampliar y ver zonas coincidentes"
        />
      ) : (
        <div className="elements-result-thumb placeholder" />
      )}
      <div className="elements-result-info">
        <span className="elements-result-name">{result.filename}</span>
        <div className="elements-result-coverage">
          {selection.map((selIdx, order) => {
            const match = result.matched_elements?.find((m) => m.query_idx === order);
            return (
              <span
                key={order}
                className={`elements-coverage-chip ${match ? 'hit' : 'miss'}`}
                title={match ? `Coincide (score ${match.score.toFixed(3)})` : 'Sin coincidencia'}
              >
                {match ? '✓' : '✗'} Zona {order + 1}
              </span>
            );
          })}
        </div>
        {(result.invoice && Object.keys(result.invoice).length > 0) && (
          <div className="elements-result-business">
            {result.invoice.cliente && <span title="Cliente del pedido">{result.invoice.cliente}</span>}
            {result.invoice.numero_pedido && <span title="Nº de pedido/documento">Pedido {result.invoice.numero_pedido}</span>}
            {result.invoice.fecha_documento && <span title="Fecha del documento">{result.invoice.fecha_documento}</span>}
            {result.invoice.articulo && (
              <ReferenceHistory
                reference={result.invoice.articulo}
                count={result.reference_count}
              />
            )}
          </div>
        )}
        <span className="elements-result-score">
          Puntuación global: {(result.score || 0).toFixed(3)}
          {result.analyzed_at && <span className="elements-result-date"> · analizado {result.analyzed_at.slice(0, 10)}</span>}
        </span>
        <div className="elements-result-actions">
          <button
            className="btn-embedded-action"
            onClick={() => window.open(`/analysis/${result.analysis_hash}`, '_blank')}
            title="Se abre en una pestaña nueva para no perder esta búsqueda"
          >
            Ver análisis completo ↗
          </button>
          <button className="btn-embedded-action" onClick={handleDownload} disabled={downloading}>
            {downloading ? 'Descargando…' : 'Descargar archivo'}
          </button>
          {mcpPath && (
            <button className="btn-embedded-action" onClick={handleGoToLocation} title="Se abre en una pestaña nueva para no perder esta búsqueda">
              Ir a ubicación ↗
            </button>
          )}
        </div>
        {downloadError && <span className="elements-download-error">{downloadError}</span>}
      </div>
    </div>
  );
};

// NUBBI-102 — Modal de búsqueda por elementos: selección por prioridad + modo edición de zonas
const ElementsSearchModal = ({ queryDrawingPages, excludeHash, onClose }) => {
  const [selectedPage, setSelectedPage] = useState(null);
  const [elements, setElements] = useState(null);
  const [selection, setSelection] = useState([]);
  const [loadingLayout, setLoadingLayout] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [deeper, setDeeper] = useState([]);
  const [busqueda, setBusqueda] = useState(0);
  const [error, setError] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const [activeImageUrl, setActiveImageUrl] = useState(null);

  // Filtros de negocio
  const [showFilters, setShowFilters] = useState(false);
  const [filterFolder, setFilterFolder] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterConical, setFilterConical] = useState('');

  // Careo IA en el zoom
  const [diffTexts, setDiffTexts] = useState({});
  const [diffLoading, setDiffLoading] = useState(null);

  // Criba IA de los resultados
  const [screenings, setScreenings] = useState({});
  const [noMatchesText, setNoMatchesText] = useState(null);

  // Modo edición de zonas
  const [editMode, setEditMode] = useState(false);
  const [zonesDirty, setZonesDirty] = useState(false);
  const [savingZones, setSavingZones] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [draftBox, setDraftBox] = useState(null);
  const editSnapshotRef = useRef(null);
  const dragRef = useRef(null);
  const imageWrapRef = useRef(null);

  const pages = queryDrawingPages || [];
  const activePage = selectedPage || pages[0];
  const { para: imagenesDe, images: resultImages } = useResultImages(results, busqueda);

  // Cada búsqueda es una criba nueva: se anota la generación para que las respuestas de la anterior,
  // si llegan tarde, no pinten veredictos sobre unos resultados que ya no son los suyos.
  const cribaRef = useRef({ vivo: false, lanzados: new Set(), zonas: {} });

  useEffect(() => {
    cribaRef.current.vivo = false;
    cribaRef.current = { vivo: true, lanzados: new Set(), zonas: {} };
    setScreenings({});
    setNoMatchesText(null);
  }, [busqueda]);

  // Criba: cada resultado se comprueba EN CUANTO LLEGA SU IMAGEN, sin esperar a las demás.
  // Esperar a que estuvieran las diez miniaturas retrasaba el primer veredicto hasta 30 s en
  // planos grandes, porque el admin_api tiene que renderizar un PDF por resultado.
  useEffect(() => {
    if (!results?.length || !activeImageUrl || imagenesDe !== busqueda) return;
    const criba = cribaRef.current;

    const recorteDeLaZona = (queryIdx, bbox) => {
      // la zona marcada se recorta una sola vez aunque la pidan los diez resultados a la vez
      if (!criba.zonas[queryIdx]) criba.zonas[queryIdx] = cropImageToB64(activeImageUrl, bbox);
      return criba.zonas[queryIdx];
    };

    results.forEach(async (result) => {
      const hash = result.analysis_hash;
      const match = result.matched_elements?.[0];
      const zona = elements?.[selection[match?.query_idx]];
      const paginaCandidato = resultImages[hash];
      if (criba.lanzados.has(hash) || !match?.bbox || !zona || !paginaCandidato) return;
      criba.lanzados.add(hash);
      let veredicto = null;
      try {
        const [query, candidato] = await Promise.all([
          recorteDeLaZona(match.query_idx, zona.bbox),
          cropImageToB64(paginaCandidato, match.bbox),
        ]);
        veredicto = await screenElementMatch(query, candidato);
      } catch (e) { /* ese resultado se queda sin veredicto, pero se sigue enseñando */ }
      if (criba.vivo) setScreenings((prev) => ({ ...prev, [hash]: veredicto }));
    });
  }, [results, resultImages, imagenesDe, busqueda, activeImageUrl, elements, selection]);

  // El que se intentó bajar y no tiene página no va a tener veredicto nunca: se da por cerrado,
  // o el "comprobando" no se quitaría jamás. Solo cuenta si YA se intentó: está en el mapa con
  // valor nulo. Los que aún no han llegado no están en el mapa y siguen esperando su turno.
  useEffect(() => {
    if (!results?.length || imagenesDe !== busqueda) return;
    const criba = cribaRef.current;
    const sinImagen = results.filter((r) => r.analysis_hash in resultImages
      && !resultImages[r.analysis_hash] && !criba.lanzados.has(r.analysis_hash));
    if (!sinImagen.length) return;
    sinImagen.forEach((r) => criba.lanzados.add(r.analysis_hash));
    setScreenings((prev) => ({ ...prev, ...Object.fromEntries(sinImagen.map((r) => [r.analysis_hash, null])) }));
  }, [results, resultImages, imagenesDe, busqueda]);

  const screeningDone = !!results?.length && results.every((r) => r.analysis_hash in screenings);
  const screeningFailed = screeningDone && results.every((r) => !screenings[r.analysis_hash]);
  const todoDescartado = screeningDone
    && results.every((r) => screenings[r.analysis_hash]?.verdict === 'NO');

  // Si la IA los descarta todos y quedan candidatos por mirar, se sigue hacia abajo. Que el
  // bueno caiga en el puesto 3 o en el 25 es azar: la puntuación no ordena, lo hemos medido.
  useEffect(() => {
    if (!todoDescartado || !deeper.length || imagenesDe !== busqueda) return;
    setResults((prev) => [...prev, ...deeper.slice(0, ELEMENT_BATCH)]);
    setDeeper((prev) => prev.slice(ELEMENT_BATCH));
  }, [todoDescartado, deeper, imagenesDe, busqueda]);

  // Si la IA los descarta todos, se le explica por qué, mirando el que más se acercó
  useEffect(() => {
    if (!todoDescartado || noMatchesText || imagenesDe !== busqueda || deeper.length) return;
    const criba = cribaRef.current;
    if (criba.explicando) return;  // tarda un par de segundos: que un re-render no la pida dos veces
    const primero = results[0];
    const match = primero.matched_elements?.[0];
    const zona = elements?.[selection[match?.query_idx]];
    const paginaCandidato = resultImages[primero.analysis_hash];
    if (!match?.bbox || !zona || !paginaCandidato) return;
    criba.explicando = true;
    (async () => {
      try {
        const [query, candidato] = await Promise.all([
          criba.zonas[match.query_idx] || cropImageToB64(activeImageUrl, zona.bbox),
          cropImageToB64(paginaCandidato, match.bbox),
        ]);
        const data = await explainNoElementMatches(query, candidato);
        if (criba.vivo) setNoMatchesText(data.explanation);
      } catch (e) { /* el aviso de que no hay nada ya sale sin explicación */ }
    })();
  }, [todoDescartado, noMatchesText, deeper, results, resultImages, imagenesDe, busqueda, activeImageUrl, elements, selection]);

  const profundizando = results?.length > ELEMENT_BATCH;
  const sinCoincidencias = todoDescartado && !deeper.length;

  // Los descartados al fondo, y solo cuando están todos: que no bailen mientras llegan.
  const orderedResults = screeningDone && results
    ? [...results].sort((a, b) => (VERDICT_RANK[screenings[a.analysis_hash]?.verdict] ?? 1)
                                - (VERDICT_RANK[screenings[b.analysis_hash]?.verdict] ?? 1))
    : results;

  const resolvePage = async (page) => {
    if (!page) return null;
    if (!page.imageUrl && page.analysisHash) {
      const url = `${process.env.REACT_APP_API_URL}/v1/analyses/${page.analysisHash}/image?page=${page.page_number}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) return null;
      page.imageUrl = URL.createObjectURL(await response.blob());
    }
    if (!page.imageB64 && page.imageUrl) {
      page.imageB64 = await blobUrlToB64(page.imageUrl);
    }
    return page.imageB64 ? page : null;
  };

  const loadLayout = async (page) => {
    setLoadingLayout(true);
    setError(null);
    setElements(null);
    setSelection([]);
    setResults(null);
    setActiveImageUrl(null);
    setEditMode(false);
    setZonesDirty(false);
    setSaveMessage(null);
    try {
      const resolved = await resolvePage(page);
      if (!resolved) {
        setError('No hay imagen de la página disponible');
        return;
      }
      setActiveImageUrl(resolved.imageUrl);

      // Si el análisis ya tiene sus secciones guardadas (hook/backfill), usarlas sin re-detectar
      if (excludeHash) {
        try {
          const stored = await getStoredElements(excludeHash);
          const pageElements = (stored.elements || []).filter((el) => el.page_number === page.page_number);
          if (pageElements.length > 0) {
            setElements(pageElements.map((el) => ({ type: el.type, bbox: el.bbox })));
            return;
          }
        } catch (e) { /* sin guardados: detectar al vuelo */ }
      }

      const data = await getElementsLayout(resolved.imageB64);
      setElements(data.elements || []);
    } catch (e) {
      setError('Error detectando las zonas del plano');
    } finally {
      setLoadingLayout(false);
    }
  };

  useEffect(() => {
    if (pages.length > 0) loadLayout(pages[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page) => {
    setSelectedPage(page);
    loadLayout(page);
  };

  const toggleElement = (idx) => {
    if (editMode) return;
    setResults(null);
    setSelection((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const handleSearch = async () => {
    if (!selection.length) return;
    setSearching(true);
    setError(null);
    // Las imágenes de los resultados anteriores se liberan al llegar los nuevos: si el zoom se
    // quedara abierto, estaría enseñando una imagen ya revocada
    setZoomImage(null);
    try {
      const resolved = await resolvePage(activePage);
      if (!resolved) {
        setError('No hay imagen de la página disponible');
        return;
      }
      const boxes = selection.map((idx) => ({
        bbox: elements[idx].bbox,
        element_type: elements[idx].type,
      }));
      const filters = {};
      if (filterFolder.trim()) filters.folder = filterFolder.trim();
      if (filterFrom) filters.date_from = filterFrom;
      if (filterTo) filters.date_to = filterTo;
      if (filterConical !== '') filters.is_conical = Number(filterConical);
      const data = await searchByElements(resolved.imageB64, boxes, excludeHash, Object.keys(filters).length ? filters : null);
      setBusqueda((n) => n + 1);
      setDeeper(data.deeper || []);
      setResults(data.results || []);
    } catch (e) {
      setError('Error en la búsqueda por elementos');
    } finally {
      setSearching(false);
    }
  };

  // ------------------------- Modo edición -------------------------

  const enterEditMode = () => {
    editSnapshotRef.current = JSON.parse(JSON.stringify(elements || []));
    setEditMode(true);
    setSelection([]);
    setResults(null);
    setSaveMessage(null);
  };

  const cancelEditMode = () => {
    setElements(editSnapshotRef.current || []);
    setEditMode(false);
    setZonesDirty(false);
    setDraftBox(null);
    dragRef.current = null;
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    setError(null);
    try {
      const resolved = await resolvePage(activePage);
      if (!resolved) return;
      const data = await getElementsLayout(resolved.imageB64);
      setElements(data.elements || []);
      setZonesDirty(true);
    } catch (e) {
      setError('Error recalculando las zonas');
    } finally {
      setRecalculating(false);
    }
  };

  const handleDeleteZone = (idx) => {
    setElements((prev) => prev.filter((_, i) => i !== idx));
    setZonesDirty(true);
  };

  const handleSaveZones = async () => {
    if (!excludeHash || !activePage) return;
    setSavingZones(true);
    setError(null);
    try {
      const resolved = await resolvePage(activePage);
      if (!resolved) return;
      const payload = (elements || []).map((el) => ({ type: el.type, bbox: el.bbox }));
      const data = await replaceElements(excludeHash, activePage.page_number, resolved.imageB64, payload);
      setZonesDirty(false);
      setEditMode(false);
      setSaveMessage(`Zonas guardadas (${data.saved})`);
    } catch (e) {
      setError('Error guardando las zonas');
    } finally {
      setSavingZones(false);
    }
  };

  const relativeCoords = (event) => {
    const rect = imageWrapRef.current.getBoundingClientRect();
    const x = Math.min(1000, Math.max(0, ((event.clientX - rect.left) / rect.width) * 1000));
    const y = Math.min(1000, Math.max(0, ((event.clientY - rect.top) / rect.height) * 1000));
    return [x, y];
  };

  const handleEditMouseDown = (event) => {
    if (!editMode) return;
    const point = relativeCoords(event);
    const handleTarget = event.target.closest('.elements-box-resize');
    const boxTarget = event.target.closest('.elements-box');
    if (handleTarget) {
      const idx = Number(handleTarget.dataset.idx);
      dragRef.current = { mode: 'resize', idx, orig: [...elements[idx].bbox] };
    } else if (boxTarget && boxTarget.dataset.idx !== undefined) {
      const idx = Number(boxTarget.dataset.idx);
      dragRef.current = { mode: 'move', idx, start: point, orig: [...elements[idx].bbox], moved: false };
    } else {
      dragRef.current = { mode: 'create', start: point };
    }
  };

  const handleEditMouseMove = (event) => {
    if (!editMode || !dragRef.current) return;
    const [x, y] = relativeCoords(event);
    const drag = dragRef.current;
    if (drag.mode === 'create') {
      const [sx, sy] = drag.start;
      const rect = [Math.min(sx, x), Math.min(sy, y), Math.max(sx, x), Math.max(sy, y)];
      drag.rect = rect;
      setDraftBox(rect);
    } else if (drag.mode === 'move') {
      const [sx, sy] = drag.start;
      const dx = x - sx;
      const dy = y - sy;
      if (Math.abs(dx) + Math.abs(dy) > 2) drag.moved = true;
      const [x1, y1, x2, y2] = drag.orig;
      const w = x2 - x1;
      const h = y2 - y1;
      const nx1 = Math.min(1000 - w, Math.max(0, x1 + dx));
      const ny1 = Math.min(1000 - h, Math.max(0, y1 + dy));
      setElements((prev) => prev.map((el, i) => (i === drag.idx ? { ...el, bbox: [nx1, ny1, nx1 + w, ny1 + h] } : el)));
    } else if (drag.mode === 'resize') {
      const [x1, y1] = drag.orig;
      setElements((prev) => prev.map((el, i) => (
        i === drag.idx ? { ...el, bbox: [x1, y1, Math.max(x1 + MIN_BOX_SIDE, x), Math.max(y1 + MIN_BOX_SIDE, y)] } : el
      )));
    }
  };

  const handleEditMouseUp = () => {
    if (!editMode || !dragRef.current) return;
    const drag = dragRef.current;
    const rect = drag.rect;
    if (drag.mode === 'create' && rect && (rect[2] - rect[0]) > MIN_BOX_SIDE && (rect[3] - rect[1]) > MIN_BOX_SIDE) {
      setElements((prev) => [...(prev || []), { type: 'manual', bbox: rect }]);
      setZonesDirty(true);
    } else if ((drag.mode === 'move' && drag.moved) || drag.mode === 'resize') {
      setZonesDirty(true);
    }
    dragRef.current = null;
    setDraftBox(null);
  };

  const handleExplainDiff = async (match, zoom) => {
    const elementIdx = selection[match.query_idx];
    const queryElement = elements?.[elementIdx];
    if (!queryElement || !zoom?.url) return;
    setDiffLoading(match.query_idx);
    try {
      const queryB64 = await cropImageToB64(activeImageUrl || activePage.imageUrl, queryElement.bbox);
      const candidateB64 = await cropImageToB64(zoom.url, match.bbox);
      const data = await explainElementsDiff(queryB64, candidateB64);
      setDiffTexts((prev) => ({ ...prev, [match.query_idx]: data.explanation }));
    } catch (e) {
      setDiffTexts((prev) => ({ ...prev, [match.query_idx]: 'No se pudo analizar la diferencia.' }));
    } finally {
      setDiffLoading(null);
    }
  };

  // Al ampliar: limpiar careos anteriores y lanzar el análisis IA de cada zona coincidente
  const openZoom = async (zoom) => {
    setDiffTexts({});
    setDiffLoading(null);
    setZoomImage(zoom);
    const matched = (zoom.matched || []).filter((m) => m.bbox && m.page_number === zoom.page);
    for (const match of matched) {
      // secuencial: no saturar el modelo con varias zonas a la vez
      // eslint-disable-next-line no-await-in-loop
      await handleExplainDiff(match, zoom);
    }
  };

  return (
    <div className="elements-modal-overlay fullscreen" onClick={onClose}>
      <div className="elements-modal fullscreen" onClick={(e) => e.stopPropagation()}>
        <div className="elements-modal-header compact">
          <h2 title="Marca zonas del plano por orden de importancia y busca piezas que coincidan en ellas">Búsqueda por elementos</h2>

          {pages.length > 1 && (
            <div className="elements-search-pages">
              {pages.map((page) => (
                <button
                  key={page.page_number}
                  className={`elements-page-btn ${activePage?.page_number === page.page_number ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                  disabled={editMode}
                  title={`Ver la página ${page.page_number} del plano`}
                >
                  Pág. {page.page_number}
                </button>
              ))}
            </div>
          )}

          <div className="elements-topbar-actions">
            {!editMode && (
              <>
                <button
                  className="elements-topbar-btn"
                  onClick={() => setShowFilters(!showFilters)}
                  title="Limitar la búsqueda por carpeta de cliente, rango de fechas o tipo de pieza (cónica/recta)"
                >
                  Filtros{(filterFolder || filterFrom || filterTo || filterConical !== '') ? ' •' : ''}
                </button>
                <button
                  className="elements-topbar-btn primary"
                  disabled={!selection.length || searching}
                  onClick={handleSearch}
                  title="Busca planos que coincidan en las zonas seleccionadas, en tu orden de prioridad (la zona 1 pesa más)"
                >
                  {searching ? 'Buscando…' : `Buscar (${selection.length})`}
                </button>
                {selection.length > 0 && !searching && (
                  <button
                    className="elements-topbar-btn"
                    onClick={() => { setSelection([]); setResults(null); }}
                    title="Quitar todas las zonas seleccionadas"
                  >
                    Limpiar
                  </button>
                )}
                <button
                  className="elements-topbar-btn"
                  onClick={enterEditMode}
                  disabled={loadingLayout || !elements}
                  title="Corregir las zonas: crear (arrastrar en zona libre), mover (arrastrar una caja), redimensionar (esquina inferior derecha) o eliminar (✕). Al guardar se actualiza la base de datos"
                >
                  Editar zonas
                </button>
              </>
            )}
            {editMode && (
              <>
                <button
                  className="elements-topbar-btn"
                  onClick={handleRecalculate}
                  disabled={recalculating || savingZones}
                  title="Volver a detectar las zonas con IA (no guarda nada hasta que pulses Guardar)"
                >
                  {recalculating ? 'Recalculando…' : 'Recalcular con IA'}
                </button>
                {excludeHash && (
                  <button
                    className="elements-topbar-btn primary"
                    onClick={handleSaveZones}
                    disabled={!zonesDirty || savingZones || recalculating}
                    title="Sustituye las zonas guardadas de esta página por las que ves ahora (crops y embeddings en base de datos)"
                  >
                    {savingZones ? 'Guardando…' : 'Guardar'}
                  </button>
                )}
                <button
                  className="elements-topbar-btn"
                  onClick={cancelEditMode}
                  disabled={savingZones}
                  title="Descartar los cambios y volver a las zonas anteriores"
                >
                  Cancelar
                </button>
                {zonesDirty && <span className="elements-save-pending">Sin guardar</span>}
              </>
            )}
            <button className="elements-modal-close" onClick={onClose} title="Cerrar la búsqueda por elementos">✕ Cerrar</button>
          </div>
        </div>

        {showFilters && !editMode && (
          <div className="elements-filters-bar">
            <label title="Solo planos cuya carpeta o ruta contenga este texto (ej. el nombre del cliente)">
              Carpeta
              <input type="text" value={filterFolder} onChange={(e) => setFilterFolder(e.target.value)} placeholder="ej. ETXETAR" />
            </label>
            <label title="Solo planos analizados desde esta fecha">
              Desde
              <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
            </label>
            <label title="Solo planos analizados hasta esta fecha">
              Hasta
              <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
            </label>
            <label title="Solo piezas del tipo indicado (clasificación cónico/recto)">
              Tipo
              <select value={filterConical} onChange={(e) => setFilterConical(e.target.value)}>
                <option value="">Todas</option>
                <option value="1">Cónicas</option>
                <option value="0">Rectas</option>
              </select>
            </label>
          </div>
        )}

        {error && <div className="elements-search-error">{error}</div>}
        {saveMessage && <div className="elements-save-ok bar">{saveMessage}</div>}

        <div className="elements-fullscreen-body">
          <div className="elements-image-area">
            {loadingLayout && (
              <div className="elements-search-loading">
                <span className="elements-spinner" />
                <span>
                  Cargando las zonas del plano…
                  <br /><small>Si es la primera vez, la detección con IA puede tardar de 15 a 30 segundos.</small>
                </span>
              </div>
            )}
            {elements && activePage && (
              <div
                className={`elements-search-image-wrap ${editMode ? 'editing' : ''}`}
                ref={imageWrapRef}
                onMouseDown={handleEditMouseDown}
                onMouseMove={handleEditMouseMove}
                onMouseUp={handleEditMouseUp}
                onMouseLeave={handleEditMouseUp}
              >
                <img src={activeImageUrl || activePage.imageUrl} alt="Plano" draggable={false} />
                {draftBox && (
                  <div
                    className="elements-box draft"
                    style={{
                      left: `${draftBox[0] / 10}%`,
                      top: `${draftBox[1] / 10}%`,
                      width: `${(draftBox[2] - draftBox[0]) / 10}%`,
                      height: `${(draftBox[3] - draftBox[1]) / 10}%`,
                    }}
                  />
                )}
                {elements.map((el, idx) => {
                  const [x1, y1, x2, y2] = el.bbox;
                  const order = selection.indexOf(idx);
                  const isSelected = order >= 0;
                  return (
                    <div
                      key={idx}
                      data-idx={idx}
                      className={`elements-box ${isSelected ? 'selected' : ''} ${editMode ? 'editable' : ''}`}
                      style={{
                        left: `${x1 / 10}%`,
                        top: `${y1 / 10}%`,
                        width: `${(x2 - x1) / 10}%`,
                        height: `${(y2 - y1) / 10}%`,
                        borderColor: TYPE_COLORS[el.type] || '#808080',
                      }}
                      onClick={() => toggleElement(idx)}
                      title={editMode ? 'Arrastra para mover' : `${TYPE_LABELS[el.type] || el.type} — clic para seleccionar/deseleccionar`}
                    >
                      {isSelected && !editMode && <span className="elements-box-order">{order + 1}</span>}
                      <span className="elements-box-label" style={{ background: TYPE_COLORS[el.type] || '#808080' }}>
                        {TYPE_LABELS[el.type] || el.type}
                      </span>
                      {editMode && (
                        <>
                          <button
                            className="elements-box-delete"
                            title="Eliminar zona"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); handleDeleteZone(idx); }}
                          >
                            ✕
                          </button>
                          <span className="elements-box-resize" data-idx={idx} title="Redimensionar" />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="elements-results-panel">
            {editMode ? (
              <>
                <h4 className="elements-results-title">Edición de zonas</h4>
                <ul className="elements-edit-help">
                  <li>Arrastra en una zona libre para <strong>crear</strong> un rectángulo</li>
                  <li>Arrastra una caja para <strong>moverla</strong></li>
                  <li>Tira de la esquina inferior derecha para <strong>redimensionar</strong></li>
                  <li>Pulsa la ✕ de una caja para <strong>eliminarla</strong></li>
                  <li><strong>Recalcular con IA</strong> vuelve a detectar las zonas desde cero</li>
                  <li>Nada se guarda hasta que pulses <strong>Guardar</strong></li>
                </ul>
                {zonesDirty && <p className="elements-save-pending">Tienes cambios sin guardar.</p>}
              </>
            ) : (
              <>
                <h4 className="elements-results-title" title="El orden de selección es la prioridad: la zona 1 es la que más pesa en la búsqueda">
                  Tu selección (por prioridad)
                </h4>
                {selection.length === 0 ? (
                  <p className="elements-sidebar-empty">
                    Haz clic en las zonas del plano en orden de importancia (la primera es la que más te interesa) y pulsa Buscar.
                  </p>
                ) : (
                  <ol className="elements-priority-list">
                    {selection.map((idx, order) => (
                      <li key={idx}>
                        <span className="elements-priority-num">{order + 1}</span>
                        <span className="elements-priority-dot" style={{ background: TYPE_COLORS[elements?.[idx]?.type] || '#808080' }} />
                        {TYPE_LABELS[elements?.[idx]?.type] || elements?.[idx]?.type}
                        <button className="elements-priority-remove" onClick={() => toggleElement(idx)} title="Quitar de la selección">✕</button>
                      </li>
                    ))}
                  </ol>
                )}

                <h4 className="elements-results-title" title="Los planos que coinciden en más zonas van primero; a igualdad, pesa más la zona 1">
                  {results === null
                    ? 'Resultados'
                    : (results.length === 0 || sinCoincidencias)
                      ? 'Sin coincidencias'
                      : `Resultados (${results.length})`}
                </h4>
                {searching && (
                  <div className="elements-search-loading">
                    <span className="elements-spinner" />
                    <span>
                      Buscando en {selection.length} zona{selection.length === 1 ? '' : 's'}…
                      <br /><small>Se compara cada zona contra las secciones de todos los planos de tu cuenta.</small>
                    </span>
                  </div>
                )}
                {!searching && results === null && selection.length > 0 && (
                  <p className="elements-sidebar-empty">Pulsa Buscar cuando tengas tu selección.</p>
                )}
                {!searching && results?.length > 0 && !screeningDone && (
                  <div className="elements-screening">
                    <span className="elements-spinner" />
                    <span>{profundizando ? 'Examinando con más profundidad…' : 'Comprobando los resultados con IA…'}</span>
                  </div>
                )}
                {screeningFailed && (
                  <div className="elements-screening">
                    <span>No se ha podido comprobar con IA; los resultados salen sin revisar.</span>
                  </div>
                )}
                {sinCoincidencias && (
                  <div className="elements-no-matches">
                    <span className="elements-no-matches-title">No hay ninguna pieza igual a esta</span>
                    {noMatchesText
                      ? <p>{noMatchesText}</p>
                      : <p>Lo que se parece es el dibujo, no la pieza.</p>}
                  </div>
                )}
                {!searching && orderedResults?.map((r) => (
                  <ResultCard
                    key={r.analysis_hash}
                    result={r}
                    selection={selection}
                    onZoom={openZoom}
                    blobUrl={resultImages[r.analysis_hash]}
                    screening={screenings[r.analysis_hash]}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {zoomImage?.url && (
        <div className="elements-zoom-overlay" onClick={(e) => { e.stopPropagation(); setZoomImage(null); }}>
          <div className="elements-zoom-compare">
            <div className="elements-zoom-panel">
              <span className="elements-zoom-title">Tu plano</span>
              <div className="elements-zoom-panel-body">
                <div className="elements-zoom-stage">
                <img src={activeImageUrl || activePage?.imageUrl} alt="Tu plano" />
                {selection.map((idx, order) => {
                  const el = elements?.[idx];
                  if (!el) return null;
                  const [x1, y1, x2, y2] = el.bbox;
                  return (
                    <div
                      key={idx}
                      className="elements-zoom-box query"
                      style={{
                        left: `${x1 / 10}%`,
                        top: `${y1 / 10}%`,
                        width: `${(x2 - x1) / 10}%`,
                        height: `${(y2 - y1) / 10}%`,
                        borderColor: TYPE_COLORS[el.type] || '#808080',
                      }}
                    >
                      <span className="elements-zoom-box-label" style={{ background: TYPE_COLORS[el.type] || '#808080' }}>
                        Zona {order + 1}
                      </span>
                    </div>
                  );
                })}
              </div>
              </div>
            </div>
            <div className="elements-zoom-panel">
              <span className="elements-zoom-title">{zoomImage.filename}</span>
              <div className="elements-zoom-panel-body">
                <div className="elements-zoom-stage">
                <img src={zoomImage.url} alt={zoomImage.filename} />
                {(zoomImage.matched || [])
                  .filter((m) => m.bbox && m.page_number === zoomImage.page)
                  .map((m) => {
                    const [x1, y1, x2, y2] = m.bbox;
                    return (
                      <div
                        key={m.query_idx}
                        className="elements-zoom-box"
                        style={{
                          left: `${x1 / 10}%`,
                          top: `${y1 / 10}%`,
                          width: `${(x2 - x1) / 10}%`,
                          height: `${(y2 - y1) / 10}%`,
                        }}
                      >
                        <span className="elements-zoom-box-label">Tu zona {m.query_idx + 1} · {(m.score || 0).toFixed(3)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="elements-zoom-diffs" onClick={(e) => e.stopPropagation()}>
            {(zoomImage.matched || [])
              .filter((m) => m.bbox && m.page_number === zoomImage.page)
              .map((m) => (
                <div key={m.query_idx} className="elements-zoom-diff-row">
                  <span className="elements-zoom-diff-title">Zona {m.query_idx + 1} · comparación IA</span>
                  {diffLoading === m.query_idx && (
                    <div className="elements-zoom-diff-text loading">
                      <span className="elements-spinner" /> Analizando diferencias…
                    </div>
                  )}
                  {diffTexts[m.query_idx] && <DiffCard text={diffTexts[m.query_idx]} />}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ElementsSearchModal;
